import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ErrorIcon from "@material-ui/icons/Error";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import WarningIcon from "@material-ui/icons/Warning";

// Paleta categórica de referência (ordem fixa; nunca reciclar cores).
const SERIES = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"]
};

// Cores de status reservadas: sempre acompanhadas de ícone e rótulo.
export const STATUS_COLORS = { warning: "#fab219", serious: "#ec835a", critical: "#d03b3b", good: "#0ca30c" };

export const BLOCKS = ["Início", "Levantamento", "Preparação", "Treinamento", "Produção", "Encerramento"];

export const normalize = value => String(value || "")
  .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export const isDark = theme => theme.palette.type === "dark";

export const seriesColor = (theme, index) => SERIES[isDark(theme) ? "dark" : "light"][index % 8];

export const blockColor = (theme, block) => {
  const index = BLOCKS.findIndex(item => normalize(item) === normalize(block));
  return index >= 0 ? seriesColor(theme, index) : chartInk(theme).muted;
};

export const typeColor = (theme, types, type) => {
  const index = types.findIndex(item => normalize(item) === normalize(type));
  return index >= 0 && index < 8 ? seriesColor(theme, index) : chartInk(theme).muted;
};

export const chartInk = theme => (isDark(theme)
  ? { grid: "#2c2c2a", axis: "#383835", muted: "#898781", secondary: "#c3c2b7", primary: "#ffffff" }
  : { grid: "#e1e0d9", axis: "#c3c2b7", muted: "#898781", secondary: "#52514e", primary: "#0b0b0b" });

export const axisProps = theme => ({
  stroke: chartInk(theme).axis,
  tick: { fill: chartInk(theme).muted, fontSize: 11 },
  tickLine: false
});

export const legendFormatter = theme => value => <span style={{ color: chartInk(theme).secondary }}>{value}</span>;

export const dateLabel = value => (value ? value.slice(0, 10).split("-").reverse().join("/") : "-");

export const numberLabel = (value, digits = 1) => (value === null || value === undefined || Number.isNaN(Number(value))
  ? "-"
  : Number(value).toLocaleString("pt-BR", { maximumFractionDigits: digits }));

export const hoursLabel = minutes => (minutes === null || minutes === undefined ? "-" : `${numberLabel(minutes / 60)} h`);

export const daysLabel = days => (days === null || days === undefined ? "-" : `${numberLabel(days)} ${Math.abs(days) === 1 ? "dia" : "dias"}`);

export const toLocalDate = value => (value ? new Date(`${value.slice(0, 10)}T00:00:00`) : null);

const useStyles = makeStyles(theme => ({
  panel: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    boxShadow: "none",
    minWidth: 0
  },
  panelHeader: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: theme.spacing(1), marginBottom: theme.spacing(1.5), flexWrap: "wrap"
  },
  panelTitle: { fontWeight: 600, fontSize: 15, lineHeight: 1.3 },
  panelSubtitle: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 2 },
  statCard: {
    position: "relative", minHeight: 96, padding: theme.spacing(1.75, 2),
    overflow: "hidden", border: `1px solid ${theme.palette.divider}`, borderRadius: 10, boxShadow: "none",
    "&:before": {
      content: '""', position: "absolute", top: 0, bottom: 0, left: 0, width: 4,
      backgroundColor: props => props.accent || theme.palette.primary.main
    }
  },
  statLabel: { color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.35 },
  statValue: { color: theme.palette.text.primary, fontSize: 25, fontWeight: 700, lineHeight: 1.2, marginTop: theme.spacing(0.75) },
  statDetail: { color: theme.palette.text.secondary, fontSize: 11, marginTop: 4 },
  empty: { color: theme.palette.text.secondary, fontSize: 13, padding: theme.spacing(2, 0) },
  table: {
    "& th": { fontWeight: 600, fontSize: 12, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    "& td": { fontSize: 13 },
    "& td.num, & th.num": { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }
  },
  tooltip: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.25),
    fontSize: 12,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[2],
    maxWidth: 280
  },
  tooltipTitle: { fontWeight: 600, marginBottom: 4 },
  tooltipRow: { display: "flex", alignItems: "center", gap: 6, fontVariantNumeric: "tabular-nums" },
  swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
  severity: { display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }
}));

export const Panel = ({ title, subtitle, actions, children, className }) => {
  const classes = useStyles();
  return (
    <Paper className={`${classes.panel} ${className || ""}`} component="section" aria-label={title}>
      {(title || actions) && (
        <div className={classes.panelHeader}>
          <div>
            {title && <Typography component="h3" className={classes.panelTitle}>{title}</Typography>}
            {subtitle && <div className={classes.panelSubtitle}>{subtitle}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </Paper>
  );
};

export const StatCard = ({ label, value, detail, accent }) => {
  const classes = useStyles({ accent });
  return (
    <Paper className={classes.statCard}>
      <div className={classes.statLabel}>{label}</div>
      <div className={classes.statValue}>{value}</div>
      {detail && <div className={classes.statDetail}>{detail}</div>}
    </Paper>
  );
};

export const EmptyState = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.empty}>{children}</div>;
};

export const OsLink = ({ id }) => (
  <Link component={RouterLink} to={`/ordens-servico/${id}`} aria-label={`Abrir OS ${id}`}>#{id}</Link>
);

export const DataTable = ({ columns, rows, rowKey, emptyText = "Nenhum registro.", maxHeight }) => {
  const classes = useStyles();
  if (!rows.length) return <EmptyState>{emptyText}</EmptyState>;
  return (
    <TableContainer style={maxHeight ? { maxHeight } : undefined}>
      <Table size="small" stickyHeader={Boolean(maxHeight)} className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.key} className={column.numeric ? "num" : undefined}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={rowKey ? rowKey(row) : index} hover>
              {columns.map(column => (
                <TableCell key={column.key} className={column.numeric ? "num" : undefined}>
                  {column.render ? column.render(row) : row[column.key] ?? "-"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const ChartTooltipBox = ({ title, rows }) => {
  const classes = useStyles();
  return (
    <div className={classes.tooltip}>
      {title && <div className={classes.tooltipTitle}>{title}</div>}
      {rows.map(row => (
        <div key={row.label} className={classes.tooltipRow}>
          {row.color && <span className={classes.swatch} style={{ background: row.color }} />}
          <span>{row.label}:</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </div>
  );
};

const SEVERITY = {
  amarelo: { label: "Atenção", color: STATUS_COLORS.warning, Icon: WarningIcon },
  laranja: { label: "Alto", color: STATUS_COLORS.serious, Icon: ReportProblemIcon },
  vermelho: { label: "Crítico", color: STATUS_COLORS.critical, Icon: ErrorIcon }
};

export const SeverityLabel = ({ level }) => {
  const classes = useStyles();
  const theme = useTheme();
  const severity = SEVERITY[level] || SEVERITY.amarelo;
  const { Icon } = severity;
  return (
    <span className={classes.severity}>
      <Icon style={{ color: severity.color, fontSize: 18 }} aria-hidden="true" />
      <span style={{ color: theme.palette.text.primary }}>{severity.label}</span>
    </span>
  );
};
